import * as React from "react";
import { CategoricalChartState } from "recharts/types/chart/types";
import usePrevious from "./usePrevious";

const useChartZoom = <T>(initialState: IChartState, data: number[], getAxisYDomain: (from: number, to: number, offset: number) => IYAxisDomain): IChartZoom => {

    const [state, setState] = React.useState<IChartState>(initialState);

    const [mouseDownRefArea, setMouseDownRefArea] = React.useState<MouseRefType>();
    const [mouseMovedRefArea, setMouseMovedRefArea] = React.useState<MouseRefType>();
    const [currentMouseRef, setCurrentMouseRef] = React.useState<MouseRefType>();
    const [scrollZoom, setScrollZoom] = React.useState<ZoomDirection | undefined>();

    const previousVisibleData = usePrevious<Array<T>>([]);

    const updateScrollZoom = () => {
        // We can only start next zoom in after the previous data has been updated to the state.
        if (previousVisibleData?.length === state.visibleData.length) {
            return;
        }

        // Zoom in focuses on the mouse
        if (scrollZoom == ZoomDirection.in) {
            // Only allow zooming in if over 20 items in chart
            if (state.visibleData.length > 20) {
                // From currently visible items in the chart find the index from where the mouse was located when zoom-in was started
                const selectedIndex = state.visibleData.findIndex((vd) => vd === currentMouseRef);

                // zoom in by 20% at a time. 
                const zoomLevel = 0.2;

                // Get the amount of items to remove from the graph
                // For example: 100 visible items => 20 items to remove
                const itemsToRemove = Math.floor(state.visibleData.length * zoomLevel);

                // Calculate the distance from the start of visible items to where the cursor currently is
                // For example 100 items, the selected item was at index 40 => distance factor from start of 0,4... 
                // This means we will remove 8 items from the start and 12 items from the end as we are closer to the start when zooming.
                let distanceFactor = selectedIndex / (state.visibleData.length);

                // Calculate a "safety" margin from the end zones as we probably want to zoom there and not skip the first / last value
                // If zoom in click was within 10% of the end zones we load all values in that end 
                if (distanceFactor < 0.1) {
                    distanceFactor = 0;
                } else if (distanceFactor > 0.9) {
                    distanceFactor = 1;
                }

                // The distance factor can cause the algorithm to try to remove too many items from the start, so never remove more than total items to remove
                const itemsToRemoveFromStart = Math.min(Math.floor(itemsToRemove * distanceFactor), itemsToRemove);
                const itemsToRemoveFromEnd = itemsToRemove - itemsToRemoveFromStart;

                // left and right boundrary for the zoom
                zoom(state.visibleData[itemsToRemoveFromStart], state.visibleData[(state.visibleData.length - 1) - itemsToRemoveFromEnd]);
            }
        }
        // Zoom out will always just zoom equal amounts to right and left of the current selection. Unline zoom in that will focus on where the mouse is
        if (scrollZoom === ZoomDirection.out) {
            // Don't try to zoom out if we are already at max-zoomout
            if (state.visibleData.length !== data.length) {
                // From currently visible items in the chart find the index from where the mouse was located when zoom-in was started
                const visibleItemIndex = data.findIndex((vd) => vd === currentMouseRef);
                // zoom out by 20% at a time. 
                const zoomLevel = 1.2;

                const newVisibleItemLength = Math.floor(state.visibleData.length * zoomLevel);
                let itemsToAddPerSide = Math.floor(newVisibleItemLength / 2);

                // by minimum add 10 items on each side as the zoom takes too long otherwise
                if (itemsToAddPerSide < 10) {
                    itemsToAddPerSide = 10;
                }

                let startIndex = visibleItemIndex - itemsToAddPerSide;
                let endIndex = visibleItemIndex + itemsToAddPerSide;

                // If we are close to the left edge
                if (startIndex < 0) {
                    // Move end index a bit less to keep the zoom consistent
                    endIndex = endIndex - startIndex;
                    // don't move the zoom further than the last item in the chart data.
                    if (endIndex >= data.length) {
                        endIndex = data.length - 1;
                    }
                    startIndex = 0;
                }
                // If we are close to the right edge
                if (endIndex >= data.length) {
                    // Move end index a bit less to keep the zoom consistent
                    startIndex = startIndex - (endIndex - data.length);
                    // don't move the zoom lower than the first item in the chart data.
                    if (startIndex < 0) {
                        startIndex = 0;
                    }
                    endIndex = data.length - 1;
                }
                zoom(data[startIndex], data[endIndex]);
            }
        }
        // Set zoom to false to trigger a new zoom next time a scroll happens
        setScrollZoom(undefined);
    }

    React.useEffect(() => {
        updateScrollZoom();
    }, [state.visibleData, scrollZoom]);

    const getValuesForPan = () => {
        // shift by 20% of visible items
        const shiftAmount = Math.floor(state.visibleData.length * 0.2);
        const rightIndex = data.findIndex((dp) => state.right === dp);
        const leftIndex = data.findIndex((dp) => state.left === dp);

        return [shiftAmount, rightIndex, leftIndex];
    };

    const zoom = (refAreaLeft: number, refAreaRight: number) => {
        // invert selection as we zoomed from right to left
        let left = refAreaLeft;
        let right = refAreaRight;
        if (refAreaLeft > refAreaRight) {
            left = refAreaRight;
            right = refAreaLeft;

        }
        // yAxis domain
        const [bottom, top, visibleData] = getAxisYDomain(left, right, 5);

        setState({
            left: left,
            right: right,
            top: top,
            bottom: bottom,
            visibleData: visibleData,
        })
    }

    const zoomOut = () => {
        setState(initialState);
    };

    const updateCurrentMouseRef = (ref: MouseRefType) => setCurrentMouseRef(ref);

    const handleZoomSelection = () => {
        if (mouseMovedRefArea === "" || mouseMovedRefArea == null || mouseDownRefArea == null || mouseDownRefArea === mouseMovedRefArea) {
            setMouseDownRefArea(undefined);
            setMouseMovedRefArea(undefined);
            return;
        }
        // It's quite hard to select the last items
        // if we are close to the end of the graph set that as the reference area.
        let indexOfMouseUp = state.visibleData.findIndex((vb) => vb === mouseMovedRefArea)
        const distanceFactor = indexOfMouseUp / (state.visibleData.length);
        // Calculate a "safety" margin from the end zones as we probably want to zoom there and not skip the first / last value
        // If selection mouse up was within 5% of the end zones we load all values in that end 
        if (distanceFactor < 0.05) {
            // take first item
            indexOfMouseUp = 0;
        } else if (distanceFactor > 0.95) {
            // take last item
            indexOfMouseUp = state.visibleData.length - 1
        }

        zoom(mouseDownRefArea as number, state.visibleData[indexOfMouseUp]);
        setMouseDownRefArea(undefined);
        setMouseMovedRefArea(undefined);
    };

    const handleScrollZoom = (e: React.WheelEvent<HTMLDivElement>) => {
        if (!currentMouseRef) {
            return;
        }
        if (e.deltaY < 0) {
            return setScrollZoom(ZoomDirection.in);

        }
        if (e.deltaY > 0) {
            return setScrollZoom(ZoomDirection.out)
        }
    };

    const handleMouseDown = (e: CategoricalChartState) => {
        e?.activeLabel && setMouseDownRefArea(e.activeLabel);
    };

    const handleMouseMove = (e: CategoricalChartState) => {
        setCurrentMouseRef(e?.activeLabel && !mouseDownRefArea ? e.activeLabel : undefined)
        if (mouseDownRefArea && e?.activeLabel) {
            setMouseMovedRefArea(e.activeLabel);
        }
    };

    const handleShowNewer = () => {
        const [shiftAmount, currentRightIndex, currentLeftIndex] = getValuesForPan();
        let newLeftIndex = currentLeftIndex + shiftAmount;
        let newRightIndex = currentRightIndex + shiftAmount;
        if (newRightIndex > data.length) {
            // To keep the same amount of visible item's move the left index less than the full amount
            newLeftIndex = newLeftIndex + (newRightIndex - (data.length - 1));
            newRightIndex = data.length - 1;
        }
        zoom(data[newLeftIndex], data[newRightIndex])
    };

    const handleShowOlder = () => {
        const [shiftAmount, currentRightIndex, currentLeftIndex] = getValuesForPan();
        let newLeftIndex = currentLeftIndex - shiftAmount;
        let newRightIndex = currentRightIndex - shiftAmount;
        if (newLeftIndex < 0) {
            newLeftIndex = 0;
            // newLeftIndex is negative and double negative is positive
            // To keep the same amount of visible item's move the right index less than the full amount
            newRightIndex = newRightIndex - newLeftIndex;
        }
        zoom(data[newLeftIndex], data[newRightIndex])
    };

    return {
        mouseDownRefArea,
        mouseMovedRefArea,
        state,
        updateCurrentMouseRef,
        handleScrollZoom,
        handleMouseDown,
        handleMouseMove,
        handleShowNewer,
        handleShowOlder,
        handleZoomSelection,
        zoomOut
    }
}

export enum ZoomDirection {
    in,
    out
}

export interface IChartState {
    left: string | number;
    right: string | number;
    top: string | number;
    bottom: string | number;
    visibleData: number[];
}

type MouseRefType = number | undefined | string;

export type IYAxisDomain = [number | string, number | string, number[]];

interface IChartZoom {
    mouseDownRefArea: MouseRefType;
    mouseMovedRefArea: MouseRefType;
    state: IChartState;
    updateCurrentMouseRef: (ref: MouseRefType) => void;
    handleScrollZoom: (e: React.WheelEvent<HTMLDivElement>) => void;
    handleZoomSelection: () => void;
    handleMouseDown: (e: CategoricalChartState) => void;
    handleMouseMove: (e: CategoricalChartState) => void;
    handleShowNewer: () => void;
    handleShowOlder: () => void;
    zoomOut: () => void;
}

export default useChartZoom;
